import { useMemo } from 'react';

import {
  DepartmentAccessLevelDTO,
  useGetDepartmentUsers,
  useListOrganizationUsers,
  UserDTO,
} from '@/api/trafikportalen';
import { useRoleIds } from '@/contexts/ConfigContext';
import { AdminType } from '@/types/administrator';

export interface ExtendedUserDTO extends UserDTO {
  adminType: AdminType;
  departments?: {
    department_id: string;
    access_level: DepartmentAccessLevelDTO;
  }[];
}

export const useGetAllAdmins = (): {
  isLoading: boolean;
  admins: ExtendedUserDTO[];
} => {
  const roleIds = useRoleIds();
  const { data: orgAdmins, isLoading: isOrgAdminsLoading } =
    useListOrganizationUsers();
  const { data: deptAdmins, isLoading: isDeptAdminsLoading } =
    useGetDepartmentUsers();

  const admins: ExtendedUserDTO[] = useMemo(() => {
    const admins = new Map<string, ExtendedUserDTO>();

    const deptAdminIds = new Set(deptAdmins?.map((admin) => admin.id));

    orgAdmins
      ?.filter((admin) => {
        const hasPermanentProfile = admin.user_profiles.some(
          (profile) => !profile.expires_at,
        );
        const isDepartmentAdmin = deptAdminIds.has(admin.id);
        return hasPermanentProfile || isDepartmentAdmin;
      })
      .forEach((admin) => {
        const permanentProfiles = admin.user_profiles.filter(
          (profile) => !profile.expires_at,
        );

        const hasRole = (roleId?: string) =>
          permanentProfiles.some((profile) => profile.role_id === roleId);

        let adminType: AdminType;
        if (permanentProfiles.length === 0) {
          adminType = 'deptUser';
        } else if (hasRole(roleIds.orgAdminWrite)) {
          adminType = 'orgAdmin';
        } else if (hasRole(roleIds.orgBearerManager)) {
          adminType = 'orgBearerManager';
        } else {
          adminType = 'orgReader';
        }

        admins.set(admin.id, {
          ...admin,
          adminType,
        });
      });

    deptAdmins?.forEach((admin) => {
      const newDepartment = {
        department_id: admin.department_id,
        access_level: admin.access_level,
      };
      if (admins.has(admin.id)) {
        const existing = admins.get(admin.id)!;
        if (existing.departments) {
          existing.departments.push(newDepartment);
        } else {
          existing.departments = [newDepartment];
        }
        admins.set(admin.id, existing);
      } else {
        const newAdmin = {
          id: admin.id,
          created_at: admin.created_at,
          updated_at: admin.updated_at,
          email: admin.email,
          firstname: admin.firstname,
          lastname: admin.lastname,
          user_profiles: admin.user_profiles,
          adminType: 'deptUser',
          departments: [newDepartment],
        } as ExtendedUserDTO;
        admins.set(admin.id, newAdmin);
      }
    });

    return Array.from(admins.values());
  }, [orgAdmins, deptAdmins, roleIds]);

  return {
    isLoading: isOrgAdminsLoading || isDeptAdminsLoading,
    admins,
  };
};
